import Sortable from 'sortablejs/modular/sortable.core.esm.js';

/**
 * GlyphSortable - A custom class for creating sortable tables.
 */
class GlyphSortable {
    /**
     * Constructor for the GlyphSortable class.
     * Initializes default options and prepares the class for usage.
     */
    constructor() {
        this.options = {}; // Default options (empty object for flexibility)
    }

    /**
     *
     * This class provides drag-and-drop functionality to reorder items within a container,
     * such as rows in a table or list items. It integrates with the SortableJS library to enable
     * users to move items by dragging them and automatically updates the order on the server.
     *
     * #### Example of usage with Table:
     * When you use GlyphSortable, every row need to have input with name="ids[]" `<input type="hidden" name="ids[]" value="<?php echo $items[$i]["id"]; ?>">`
     * and every row need to have handle icon `<i class="bi bi-arrows-move handle"></i>`
     * ```html
     * <form name="frmSortable" id="frmSortable" action="" method="post" enctype="application/x-www-form-urlencoded">
     *     <div class="table-responsive">
     *         <table class="table table-hover table-striped table-sm">
     *             <thead>
     *             <tr>
     *                 <th width="25"></th>
     *                 <th width="40" class="text-center">ID</th>
     *                 <th>Title <a href="#" class="toggle-sort"></a></th>
     *                 <th class="text-end" width="35"></th>
     *             </tr>
     *             </thead>
     *             <tbody id="sortableList">
     *             <?php for ($i = 0; $i < count($items); $i++): ?>
     *                 <tr>
     *                     <td class="text-center">
     *                         <i class="bi bi-arrows-move handle"></i>
     *                     </td>
     *                     <td class="text-center">
     *                         <input type="hidden" name="ids[]" value="<?php echo $items[$i]["id"]; ?>">
     *                         <?php echo $i; ?>
     *                     </td>
     *                     <td>
     *                         <a href="#" class="stretched-link"><?php echo $items[$i]["title"]; ?></a>
     *                     </td>
     *                     <td class="text-end">
     *                         <a href="#" data-href="#" data-bs-toggle="modal" data-bs-target="#deleteModal"
     *                            class="btn btn-outline-danger btn-sm btn_provjera_safe">
     *                             <i class="bi bi-trash-fill"></i>
     *                         </a>
     *                     </td>
     *                 </tr>
     *             <?php endfor; ?>
     *             </tbody>
     *         </table>
     *     </div>
     * </form>
     * ```
     *
     * ```javascript
     * const token = "your_token_here";
     * const sortableTable = new GlyphSortable();
     * sortableTable.add("#sortableTable", token, {
     *     animation: 150,
     *     handle: '.handle',
     *     onEnd: (item) => { console.log('Row reordered:', item); }
     * });
     * ```
     *
     * #### Example of usage with every other element (div, ul...):
     * ```html
     * <div id="sortableList">
     *     <div>
     *         <i class="bi bi-arrows-move handle"></i>
     *         <input type="hidden" name="ids[]" value="1">
     *             ...
     *     </div>
     *     <div>
     *         <i class="bi bi-arrows-move handle"></i>
     *         <input type="hidden" name="ids[]" value="2">
     *             ...
     *     </div>
     * </div>
     * ```
     *
     * #### Description:
     * This class uses the SortableJS library to make elements in a container (such as table rows) draggable.
     * Once a drag operation is completed, the row order is updated on the server via an API request. The `token`
     * is sent along with the request to authenticate the action.
     *
     * #### Key Options:
     * - `handle`: The selector for the handle element that is used to drag the item (default: `.handle`).
     * - `animation`: The duration of the animation when moving items (default: 150ms).
     * - `onEnd`: A callback function to be called after the drag ends (fires when the drag operation is completed).
     * - `onStart`: A callback function to be called when the drag operation starts.
     * - `onUpdate`: A callback function triggered when the list order is changed.
     * - Other SortableJS options are also supported and passed directly to the SortableJS instance.
     *
     * #### Example:
     * ```javascript
     * const token = "your_token_here";
     * const sortableTable = new GlyphSortable();
     * sortableTable.add("#sortableTable", token, {
     *     handle: '.handle',
     *     animation: 200,
     *     onEnd: (item) => {
     *         console.log('Reordered item:', item);
     *     }
     * });
     * ```
     * In this example, the `#sortableTable` container is made sortable, and the order of rows is updated via an API request.
     *
     * @param {string|HTMLElement} selector - The selector string or the DOM element to make sortable.
     * @param {string} token - The security token to send with the request when updating the row order.
     * @param {object} [options={}] - The options to customize the behavior of the sortable table.
     * @returns {Promise} Resolves with the SortableJS instance.
     */
    add(selector, token, options = {}) {
        return new Promise((resolve, reject) => {
            // Store the token and options
            this.token = token;

            // If a DOM element is passed, use it directly; otherwise, treat it as a selector
            if (selector instanceof HTMLElement) {
                this.container = selector;
            } else {
                this.container = document.querySelector(selector);
            }

            this.options = { ...this.options, ...options }; // Merge default options with custom options

            // Check if the container is valid
            if (!this.container) {
                reject(`${selector} -> Container not found. Check your selector or passed element!`);
                return;
            }

            // Initialize the sortable functionality on the container element
            const sortable = new Sortable(this.container, {
                handle: '.handle', // Specify the handle element for dragging (can be customized via options)
                animation: 150,  // ms, animation speed moving items when sorting, `0` — without animation
                easing: "cubic-bezier(1, 0, 0, 1)",
                ghostClass: "glyph-sortable-ghost",
                dragClass: "glyph-sortable-drag",
                onEnd: (e) => {
                    if (this.options.onEnd) {
                        this.options.onEnd(e.item); // Trigger the onEnd callback
                    }
                    this.debounceSendUpdate(); // Send API request after the drag ends
                },
                // Spread other custom options (if any)
                ...this.options
            });

            resolve(sortable); // Resolve the promise with the SortableJS instance
        });
    }

    /**
     * Debounced function to ensure only one request is sent.
     * This is used to delay the request to update the row order on the server.
     */
    debounceSendUpdate() {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
            // Only send the update if token is provided
            if (this.token) {
                this.sendUpdate();
            }
        }, 100); // Delay the update to ensure a single request is sent after the drag ends
    }

    /**
     * Sends an API request to the server to update the row order.
     * It collects the updated order of rows and sends them to the server.
     */
    sendUpdate() {
        if (this.container && this.container?.children.length > 0) {
            const ids = Array.from(this.container?.children)
                .map(item => {
                    const input = item.querySelector("input[name='ids[]']");
                    return input ? input.value : null;
                })
                .filter(id => id !== null);

            const formData = new FormData();
            formData.append('ids', JSON.stringify(ids));

            // Send the updated order via an API request
            fetch(APPLICATION + '/service/allgroups/sortable?' + this.token, {
                method: 'POST',
                body: formData,
            })
                .then(response => response.json())
                .then(response => {
                    showAlert(ALERT_TYPE, response);
                })
                .catch(error => {
                    console.error('Error updating positions', error);
                });
        } else {
            console.error("Invalid container or container doesn't have child elements!");
        }
    }
}

export default GlyphSortable;
